import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MiniSimItem } from '../../lib/supabase';
import { TalkingHeadAvatar } from '../talking-head/TalkingHeadAvatar';
import { useLiveAPIContext } from '../../contexts/LiveAPIContext';
import { useTranscriptStore } from '../../lib/store-transcript';
import { AudioRecorder } from '../../lib/audio-recorder';
import { Modality } from '@google/genai';
import './module-player.scss';

interface MiniSimStepProps {
    item: MiniSimItem;
    onComplete: () => void;
}

export const MiniSimStep: React.FC<MiniSimStepProps> = ({ item, onComplete }) => {
    const { client, setConfig, connected, connect, disconnect } = useLiveAPIContext();
    const [phase, setPhase] = useState<'ready' | 'configuring' | 'listening' | 'evaluating' | 'done' | 'failed'>('ready');
    const [feedback, setFeedback] = useState<string | null>(null);
    const transcripts = useTranscriptStore((s) => s.transcripts);
    const hasConnectedRef = useRef(false);
    const evaluationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [audioRecorder] = useState(() => new AudioRecorder());

    // Step 1: When user clicks Start (or auto-start), apply the config first
    const handleStart = useCallback(() => {
        useTranscriptStore.getState().clearTranscripts();
        hasConnectedRef.current = false;
        setFeedback(null);

        // Set the config for this mini simulation
        setConfig((prev) => ({
            ...prev,
            responseModalities: [Modality.AUDIO],
            outputAudioTranscription: {},
            inputAudioTranscription: {},
            systemInstruction: {
                parts: [{
                    text: `Sen çok KATI bir sınav gözetmenisin. Görevin kullanıcının cevabını değerlendirmektir.
Kullanıcıya şu soruyu sor: "${item.aiQuestion}". 
Beklenen doğru cevap kapsamında olması gerekenler: "${item.expectedAnswer}".

KURALLAR:
1. Kullanıcının cevabını dikkatle dinle.
2. Cevap beklenen cevap ile ANLAMSAL olarak örtüşüyorsa DOĞRU kabul et.
3. Eğer cevap ALAKASIZ (örn: "ekrana yumruk atarım", "bilmiyorum", "hava güzel") veya YANLIŞ ise KESİNLİKLE reddet.
4. ASLA nazik olmak için yanlış cevabı doğru kabul etme. "Ekrana yumruk atmak" gibi şiddet içeren veya saçma cevapları DERHAL başarısız say.

EYLEM:
- Kararın verildikten sonra SADECE 'finish_level' fonksiyonunu çağır. Konuşma yapma.
- Eğer Doğruysa: finish_level(true, "Tebrikler, doğru cevap.")
- Eğer Yanlışsa: finish_level(false, "Yanlış cevap. Doğrusu: ${item.expectedAnswer}")

İlk mesajında sadece soruyu sor.`
                }]
            },
            tools: [{
                functionDeclarations: [{
                    name: "finish_level",
                    description: "Call this to finish the level with success or failure status.",
                    parameters: {
                        type: "OBJECT" as any,
                        properties: {
                            success: { type: "BOOLEAN" as any },
                            feedback: { type: "STRING" as any }
                        },
                        required: ["success", "feedback"]
                    }
                }]
            }],
            speechConfig: {
                voiceConfig: {
                    prebuiltVoiceConfig: {
                        voiceName: item.voice || 'Puck'
                    }
                }
            }
        }));

        // Move to configuring phase - the connect will happen in the next effect
        setPhase('configuring');
    }, [item, setConfig]);

    // Auto-start on mount
    useEffect(() => {
        handleStart();
    }, [handleStart]);

    // Listen for tool calls to finish level silently
    useEffect(() => {
        const onToolCall = (toolCall: any) => {
            console.log("Tool call received:", toolCall);
            const fc = toolCall.functionCalls.find((fc: any) => fc.name === "finish_level");
            if (fc) {
                const args = fc.args as any;
                console.log("Level finished via tool:", args);
                // Immediately stop audio
                if (audioRecorder) audioRecorder.stop();

                // Clear any pending evaluation timer so it doesn't trigger again
                if (evaluationTimerRef.current) {
                    clearTimeout(evaluationTimerRef.current);
                    evaluationTimerRef.current = null;
                }

                if (args.success) {
                    setPhase('done');
                    setFeedback(args.feedback);
                } else {
                    setPhase('failed');
                    setFeedback(args.feedback);
                    disconnect(); // Disconnect on fail too
                }
            }
        };

        client.on("toolcall", onToolCall);
        return () => {
            client.off("toolcall", onToolCall);
        };
    }, [client, audioRecorder, disconnect]);

    // Step 2: Once config is applied and we're in configuring phase, connect
    useEffect(() => {
        if (phase !== 'configuring' || hasConnectedRef.current) return;

        // Delay to ensure React state has propagated
        const timer = setTimeout(async () => {
            hasConnectedRef.current = true;
            try {
                console.log('🎯 Mini sim connecting...');
                await connect();
                // Send initial trigger so AI speaks first and asks its question
                setTimeout(() => {
                    client.send({ text: 'Merhaba, soruyu sor.' }, true);
                    console.log('📨 Initial trigger sent to AI');
                }, 500);
                setPhase('listening');
            } catch (err) {
                console.error('Mini sim bağlantı hatası:', err);
                // If connection fails, user can try going back or retry (auto-retry logic could be added)
                setPhase('done'); // Fail-safe: just complete it to not block
                onComplete();
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [phase, connect, client, onComplete]);

    // Step 3: START MICROPHONE when connected — send audio to AI via client.sendRealtimeInput
    useEffect(() => {
        if (!connected || phase === 'ready' || phase === 'done' || phase === 'failed') {
            audioRecorder.stop();
            return;
        }

        const onData = (base64: string) => {
            client.sendRealtimeInput([
                {
                    mimeType: "audio/pcm;rate=16000",
                    data: base64,
                },
            ]);
        };

        audioRecorder
            .on("data", onData)
            .start();

        console.log('🎤 Mini sim microphone started');

        return () => {
            audioRecorder
                .off("data", onData)
                .stop();
        };
    }, [connected, phase, client, audioRecorder]);

    // Step 4: Track user speech - only as fallback timeout now
    useEffect(() => {
        if (phase !== 'listening') return;

        const userMessages = transcripts.filter(t => t.role === 'user' && t.text.trim().length > 3);
        if (userMessages.length > 0) {
            // User has spoken - give AI time to call tool
            if (!evaluationTimerRef.current) {
                evaluationTimerRef.current = setTimeout(() => {
                    // Fallback if tool call fails or AI talks instead
                    setPhase('evaluating');
                }, 8000);
            }
        }
    }, [transcripts, phase]);

    // Step 5: Handle evaluation -> done transition (fallback path)
    useEffect(() => {
        if (phase === 'evaluating') {
            // If strict evaluation fails to trigger tool call, we might assume failure or just pass?
            // Let's assume pass for fallback to avoid getting stuck, but log warning.
            // OR prompt user to try again?
            // Standard fallback:
            const timer = setTimeout(() => {
                setPhase('done');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [phase]);

    // Step 6: Handle done -> cleanup and complete
    useEffect(() => {
        if (phase === 'done') {
            audioRecorder.stop();
            // Disconnect immediately
            disconnect();

            const timer = setTimeout(() => {
                useTranscriptStore.getState().clearTranscripts();
                if (evaluationTimerRef.current) {
                    clearTimeout(evaluationTimerRef.current);
                    evaluationTimerRef.current = null;
                }
                onComplete();
            }, 1500); // 1.5s delay to see "Doğru!" message
            return () => clearTimeout(timer);
        }
    }, [phase, disconnect, onComplete, audioRecorder]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            console.log("MiniSimStep unmounting, cleaning up...");
            audioRecorder.stop();
            if (evaluationTimerRef.current) {
                clearTimeout(evaluationTimerRef.current);
            }
            // Ensure AI is disconnected when unmounting
            disconnect();
        };
    }, [audioRecorder, disconnect]);

    const handleRetry = () => {
        handleStart();
    };

    return (
        <div className="mini-sim-step">
            <div className="mini-sim-container">
                <div className="avatar-area">
                    <TalkingHeadAvatar avatarUrl={item.avatarUrl || '/avatars/julia.glb'} />
                </div>

                <div className="mini-sim-info">
                    {phase === 'ready' && (
                        <div className="listening-state">
                            <p>Hazırlanıyor...</p>
                        </div>
                    )}

                    {phase === 'configuring' && (
                        <div className="listening-state">
                            <div className="listening-indicator">
                                <div className="pulse-ring"></div>
                                <span className="material-symbols-outlined">settings</span>
                            </div>
                            <p>Bağlanılıyor...</p>
                        </div>
                    )}

                    {(phase === 'listening' || phase === 'evaluating') && (
                        <div className="listening-state">
                            <div className="listening-indicator">
                                <div className="pulse-ring"></div>
                                <span className="material-symbols-outlined">mic</span>
                            </div>
                            <p>{phase === 'listening' ? 'Dinliyor...' : 'Değerlendiriliyor...'}</p>
                        </div>
                    )}

                    {phase === 'done' && (
                        <div className="done-state">
                            <span className="material-symbols-outlined done-icon">check_circle</span>
                            <p>{feedback || 'Doğru!'}</p>
                        </div>
                    )}

                    {phase === 'failed' && (
                        <div className="failed-state" style={{ textAlign: 'center', color: '#dc2626' }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '48px', marginBottom: '8px' }}>cancel</span>
                            <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>{feedback || 'Yanlış cevap.'}</p>
                            <button
                                onClick={handleRetry}
                                className="btn-primary"
                                style={{ background: '#dc2626', border: 'none', padding: '12px 24px', borderRadius: '8px', color: 'white', cursor: 'pointer' }}
                            >
                                Tekrar Dene
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
